/**
 * UI — event bindings for all 45 controls, DOM updates, auto-mix, time display
 */

import { APIClient } from './api-client.js'
import { StorageManager } from './storage.js'
import { initializeSearchUI } from './search-ui.js'
import { DownloadManager } from './download-manager.js'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function $(id) { return document.getElementById(id) }

function on(id, event, handler) {
  const el = $(id)
  if (el) el.addEventListener(event, handler)
}

function bindRange(id, handler) {
  on(id, 'input', e => handler(parseFloat(e.target.value)))
}

function formatTime(s) {
  if (!s || !isFinite(s) || s < 0) return '0:00'
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return `${m}:${String(sec).padStart(2, '0')}`
}

function showLoading(text = 'ANALYZING BPM...') {
  $('loadingOverlay').classList.remove('hidden')
  $('loadingText').textContent = text
}

function hideLoading() {
  $('loadingOverlay').classList.add('hidden')
}

// ─── Main initializer ──────────────────────────────────────────────────────────

export function initializeUI({ engine, state, storage, deckA, deckB, vizA, vizB, analyzer, playlistA, playlistB }) {

  // ── Playlist helpers ──────────────────────────────────────────────────────

  function renderPlaylist(playlistId, playlist, deckName) {
    const container = $(playlistId)
    if (!container) return
    container.innerHTML = ''

    let draggedFromIdx = null

    playlist.getPlaylist().forEach((track, idx) => {
      const item = document.createElement('div')
      const isAnalyzing = track._analyzing === true
      const isDownloading = track._isDownloading === true
      const oppName = deckName === 'A' ? 'B' : 'A'
      const isQueued = track._queuedTo === oppName
      const downloadProgress = track._downloadProgress || 0
      const downloadStatus = track._downloadStatus || 'INITIALIZING'

      item.className = 'playlist-item'
        + (isAnalyzing ? ' analyzing' : '')
        + (isDownloading ? ' downloading' : '')
        + (isQueued    ? ' queued'    : '')
      item.draggable = !isAnalyzing && !isDownloading
      item.dataset.index = idx

      // Build meta text based on state
      const bitrateStr = track.bitrate ? ` • ${track.bitrate}kbps` : ''
      let metaText = `${track.bpm || '--'} BPM • ${track.key || '-'}${bitrateStr}`
      if (isDownloading) {
        metaText = `⬇ ${downloadProgress}% • ${downloadStatus}`
      } else if (isAnalyzing) {
        metaText = '⚙ ANALYZING...'
      }

      item.innerHTML = `
        <span class="playlist-item-drag">⋮⋮</span>
        <div class="playlist-item-info">
          <div class="playlist-item-title">${track.name || 'Unknown'}</div>
          <div class="playlist-item-meta" style="color: ${isDownloading ? 'var(--green)' : 'inherit'}">${metaText}</div>
        </div>
        ${isQueued    ? `<span class="playlist-item-queued-badge">→${oppName}</span>` : ''}
        ${isDownloading ? `<span class="playlist-item-status" style="color: var(--green)">⬇ ${downloadProgress}%</span>`
                        : isAnalyzing ? '<span class="playlist-item-status">ANALYZING</span>'
                        : '<span class="playlist-item-delete">✕</span>'}
      `

      // Single-click: load into this deck (current behaviour)
      item.addEventListener('click', (e) => {
        if (e.target.closest('.playlist-item-delete')) {
          e.stopPropagation()
          playlist.removeTrack(idx)
          renderPlaylist(playlistId, playlist, deckName)
        } else if (!e.target.closest('.playlist-item-drag')) {
          loadPlaylistTrack(track, playlist, idx, deckName === 'A' ? deckA : deckB, deckName, engine, state, analyzer, playlistA, playlistB)
        }
      })

      // Double-click: queue track into the OPPOSITE deck (cued, not playing)
      item.addEventListener('dblclick', (e) => {
        e.stopPropagation()
        if (isAnalyzing || !track.audioBuffer) return

        const oppDeck     = deckName === 'A' ? deckB : deckA
        const oppPlaylist = deckName === 'A' ? playlistB : playlistA
        const oppPlId     = deckName === 'A' ? 'playlistB' : 'playlistA'

        // Clear previous "queued" badge on this deck's playlist
        playlist.getPlaylist().forEach(t => { t._queuedTo = null })

        // Mark this track as queued to the opposite deck
        track._queuedTo = oppName

        // Load into opposite deck without playing
        loadPlaylistTrack(track, playlist, idx, oppDeck, oppName, engine, state, analyzer, playlistA, playlistB)

        // Seek to introEnd so the deck is pre-cued at the first musical moment
        if (track.introEnd > 0) {
          setTimeout(() => oppDeck.seekTo(track.introEnd), 80)
        }

        // Flash the opposite deck header to signal it's cued
        const oppPanel = $(`deck${oppName}-panel`)
        if (oppPanel) {
          oppPanel.classList.add('cued-incoming')
          setTimeout(() => oppPanel.classList.remove('cued-incoming'), 1200)
        }

        // Re-render both playlists to update badges
        renderPlaylist(playlistId, playlist, deckName)
        renderPlaylist(oppPlId, oppPlaylist, oppName)
      })

      // Right-click: context menu
      item.addEventListener('contextmenu', (e) => {
        e.preventDefault()
        e.stopPropagation()
        showContextMenu(e, track, idx, playlist, playlistId, deckName)
      })

      // Drag handlers
      item.addEventListener('dragstart', (e) => {
        draggedFromIdx = idx
        item.classList.add('dragging')
        e.dataTransfer.effectAllowed = 'move'
      })

      item.addEventListener('dragend', () => {
        item.classList.remove('dragging')
        document.querySelectorAll(`#${playlistId} .playlist-item`).forEach(el => {
          el.classList.remove('drag-over')
        })
        draggedFromIdx = null
      })

      item.addEventListener('dragover', (e) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        if (draggedFromIdx !== null && draggedFromIdx !== idx) {
          item.classList.add('drag-over')
        }
      })

      item.addEventListener('dragleave', () => {
        item.classList.remove('drag-over')
      })

      item.addEventListener('drop', (e) => {
        e.preventDefault()
        item.classList.remove('drag-over')

        if (draggedFromIdx !== null && draggedFromIdx !== idx) {
          playlist.moveTrack(draggedFromIdx, idx)
          renderPlaylist(playlistId, playlist, deckName)
        }
      })

      container.appendChild(item)
    })
  }

  // ── Context Menu ─────────────────────────────────────────────────────────

  const ctxEl = document.getElementById('ctxMenu')

  function closeContextMenu() {
    ctxEl.classList.remove('open')
    ctxEl.innerHTML = ''
  }

  // Close on outside click or Escape
  document.addEventListener('click',  closeContextMenu)
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeContextMenu() })

  function ctxItem(icon, label, action, opts = {}) {
    const el = document.createElement('div')
    el.className = 'ctx-item' + (opts.danger ? ' ctx-danger' : '') + (opts.disabled ? ' ctx-disabled' : '')
    el.setAttribute('role', 'menuitem')
    el.innerHTML = `<span class="ctx-icon">${icon}</span>${label}`
    if (!opts.disabled) {
      el.addEventListener('click', (e) => {
        e.stopPropagation()
        closeContextMenu()
        action()
      })
    }
    return el
  }

  function ctxSep() {
    const el = document.createElement('div')
    el.className = 'ctx-separator'
    return el
  }

  function ctxGroupLabel(text) {
    const el = document.createElement('div')
    el.className = 'ctx-group-label'
    el.textContent = text
    return el
  }

  function showContextMenu(e, track, idx, playlist, playlistId, deckName) {
    closeContextMenu()

    const pl          = playlist.getPlaylist()
    const isFirst     = idx === 0
    const isLast      = idx === pl.length - 1
    const hasBuffer   = !!track.audioBuffer
    const isA         = deckName === 'A'
    const oppName     = isA ? 'B' : 'A'
    const oppPlaylist = isA ? playlistB : playlistA
    const oppPlId     = isA ? 'playlistB' : 'playlistA'
    const targetDeckA = deckA
    const targetDeckB = deckB

    // ── Send to Playlist ──────────────────────────────────────────────
    ctxEl.appendChild(ctxGroupLabel('Send to Playlist'))

    ctxEl.appendChild(ctxItem('◈', `Move to Playlist ${oppName}`, () => {
      const clone = { ...track }
      playlist.removeTrack(idx)
      oppPlaylist.addTrack(clone)
      renderPlaylist(playlistId, playlist, deckName)
      renderPlaylist(oppPlId, oppPlaylist, oppName)
    }))

    // ── Load to Deck ──────────────────────────────────────────────────
    ctxEl.appendChild(ctxSep())
    ctxEl.appendChild(ctxGroupLabel('Load to Deck'))

    ctxEl.appendChild(ctxItem('A', 'Load to Deck A', () => {
      if (hasBuffer) {
        loadPlaylistTrack(track, playlist, idx, targetDeckA, 'A', engine, state, analyzer, playlistA, playlistB)
      }
    }, { disabled: !hasBuffer }))

    ctxEl.appendChild(ctxItem('B', 'Load to Deck B', () => {
      if (hasBuffer) {
        loadPlaylistTrack(track, playlist, idx, targetDeckB, 'B', engine, state, analyzer, playlistA, playlistB)
      }
    }, { disabled: !hasBuffer }))

    // ── Reorder ───────────────────────────────────────────────────────
    ctxEl.appendChild(ctxSep())
    ctxEl.appendChild(ctxGroupLabel('Reorder'))

    ctxEl.appendChild(ctxItem('↑', 'Move Up', () => {
      playlist.moveTrack(idx, idx - 1)
      renderPlaylist(playlistId, playlist, deckName)
    }, { disabled: isFirst }))

    ctxEl.appendChild(ctxItem('↓', 'Move Down', () => {
      playlist.moveTrack(idx, idx + 1)
      renderPlaylist(playlistId, playlist, deckName)
    }, { disabled: isLast }))

    // ── Analysis ──────────────────────────────────────────────────────
    ctxEl.appendChild(ctxSep())
    ctxEl.appendChild(ctxGroupLabel('Track'))

    ctxEl.appendChild(ctxItem('⚙', 'Re-analyze Track', async () => {
      if (!hasBuffer) return
      track._analyzing = true
      renderPlaylist(playlistId, playlist, deckName)

      try {
        const analysis = await analyzer.analyzeTrack(track.audioBuffer, () => {})
        const { introEnd, outroStart } = analyzer.detectIntroAndOutro(track.audioBuffer)

        // Recalculate bitrate from buffer size estimate
        const estimatedBytes = track.audioBuffer.length * track.audioBuffer.numberOfChannels * 2
        track.bitrate = Math.round((estimatedBytes * 8) / track.audioBuffer.duration / 1000)

        track.bpm        = analysis?.bpm    || track.bpm
        track.key        = analysis?.key    || track.key
        track.energy     = analysis?.energy ?? track.energy
        track.introEnd   = introEnd
        track.outroStart = outroStart
        track.analysis   = { ...analysis, introEnd, outroStart }
        track._analyzing = false

        renderPlaylist(playlistId, playlist, deckName)
        console.log(`✓ Re-analyzed: ${track.name} → ${track.bpm} BPM, ${track.key}, ${track.bitrate}kbps`)
      } catch (err) {
        track._analyzing = false
        renderPlaylist(playlistId, playlist, deckName)
        console.error('Re-analyze failed:', err)
      }
    }, { disabled: !hasBuffer }))

    // ── Danger zone ───────────────────────────────────────────────────
    ctxEl.appendChild(ctxSep())

    ctxEl.appendChild(ctxItem('✕', 'Remove from Playlist', () => {
      playlist.removeTrack(idx)
      renderPlaylist(playlistId, playlist, deckName)
    }, { danger: true }))

    // Position the menu — keep it within viewport bounds
    const vw = window.innerWidth
    const vh = window.innerHeight
    const mx = e.clientX
    const my = e.clientY

    ctxEl.classList.add('open')

    // Measure after display
    requestAnimationFrame(() => {
      const mw = ctxEl.offsetWidth
      const mh = ctxEl.offsetHeight
      ctxEl.style.left = (mx + mw > vw ? vw - mw - 8 : mx) + 'px'
      ctxEl.style.top  = (my + mh > vh ? vh - mh - 8 : my) + 'px'
    })
  }

  // ── End Context Menu ──────────────────────────────────────────────────────

  async function loadPlaylistTrack(track, playlist, idx, deck, deckName, engine, state, analyzer, plA, plB) {
    if (!deck) return
    // Load track from playlist (fast path: already decoded)
    playlist.loadTrack(idx)

    if (track.audioBuffer && track.analysis) {
      // Update vinyl art — use cached image for search tracks, file for local tracks
      if (track._imageUrl) {
        const vinylEl = $(`vinyl${deckName}`)
        if (vinylEl) {
          const img = vinylEl.querySelector('.vinyl-art') || vinylEl.querySelector('img')
          if (img) img.src = track._imageUrl
        }
      } else {
        APIClient.applyVinylArt($(`vinyl${deckName}`), track.artist, track.name, track.file)
      }

      // Fast load: use cached audio buffer
      const bpm = track.bpm || 120
      deck.loadBuffer(track.audioBuffer, bpm, track.name, track.artist)
      deck.analysis = track.analysis

      // Generate track ID and auto-load cues from database
      const trackId = await deck.generateTrackId(
        track.file || track.name,
        track.fileSize || 0,
        track.audioBuffer.duration
      )

      if (trackId && storage) {
        try {
          const savedCues = await storage.loadTrackCues(trackId)
          if (savedCues) {
            deck.restoreCueData(savedCues)
            // Update UI hot cue buttons to show restored state
            if (Array.isArray(savedCues.hotCues)) {
              for (let i = 0; i < 4; i++) {
                const btn = document.querySelector(`.hot-cue[data-deck="${deckName}"][data-index="${i}"]`)
                if (btn && savedCues.hotCues[i] !== null) {
                  btn.classList.add('has-cue')
                } else if (btn) {
                  btn.classList.remove('has-cue')
                }
              }
            }
          }
        } catch (err) {
          console.warn('Failed to load saved cues from playlist:', err)
        }
      }

      // Update UI display
      $(`trackName${deckName}`).textContent = track.name || 'Unknown'
      $(`artist${deckName}`).textContent = track.artist || ''
      $(`bpmDisplay${deckName}`).textContent = bpm + ' BPM'
      $(`keyDisplay${deckName}`).textContent = track.analysis.key || '-'
      $(`energyDisplay${deckName}`).textContent = (track.analysis.energy ? (track.analysis.energy * 100).toFixed(0) : '--') + '%'
      $(`duration${deckName}`).textContent = formatTime(track.duration)

      // Update compatibility score if both tracks loaded
      const otherDeckObj = deckName === 'A' ? deckB : deckA
      if (otherDeckObj && otherDeckObj.audioBuffer && otherDeckObj.analysis && track.analysis) {
        const score = analyzer.scoreCompatibility(track.analysis, otherDeckObj.analysis, bpm, otherDeckObj.bpm)
        $('compatibilityValue').textContent = score.toFixed(0) + '%'
      }
    }

    renderPlaylist(deckName === 'A' ? 'playlistA' : 'playlistB', playlist, deckName)
  }

  // ── Track loader (inside initializeUI so it can access renderPlaylist) ───

  const AUDIO_TYPES = ['audio/mpeg', 'audio/wav', 'audio/flac', 'audio/ogg', 'audio/aac', 'audio/mp4', 'audio/x-m4a']
  const AUDIO_EXTS  = ['.mp3', '.wav', '.flac', '.ogg', '.aac', '.m4a', '.mp4']

  async function loadTrack(file, deck, deckId, otherDeck, playlist, otherPlaylist) {
    if (!file) return

    // Validate audio file type
    const ext = '.' + file.name.split('.').pop().toLowerCase()
    if (!AUDIO_TYPES.includes(file.type) && !AUDIO_EXTS.includes(ext)) {
      alert('Unsupported file type. Please drop an audio file (MP3, WAV, FLAC, OGG, AAC, M4A).')
      return
    }

    // Resume AudioContext if suspended (browser autoplay policy)
    if (engine.ctx.state === 'suspended') await engine.ctx.resume()

    try {
      const { artist, track } = APIClient.parseFilename(file.name)

      // Add placeholder to playlist immediately (with analyzing glow)
      let placeholderIdx = -1
      const playlistContainerId = `playlist${deckId}`
      if (playlist) {
        placeholderIdx = playlist.getPlaylist().length
        playlist.addTrack({
          file: file.name,
          name: track,
          artist: artist,
          bpm: null,
          key: null,
          energy: 0,
          analysis: null,
          duration: 0,
          audioBuffer: null,
          _analyzing: true
        })
        renderPlaylist(playlistContainerId, playlist, deckId)
      }

      // Helper: update the analyzing status text on the playlist item
      function updateAnalyzingStatus(stage) {
        const container = $(playlistContainerId)
        if (!container) return
        const items = container.querySelectorAll('.playlist-item')
        const item = items[placeholderIdx]
        if (!item) return
        const statusEl = item.querySelector('.playlist-item-status')
        if (statusEl) statusEl.textContent = stage
      }

      updateAnalyzingStatus('LOADING')
      const arrayBuffer = await file.arrayBuffer()

      updateAnalyzingStatus('DECODING')
      const audioBuffer = await engine.decodeAudio(arrayBuffer)

      // Show track name immediately while analysis runs in worker
      $(`trackName${deckId}`).textContent = track
      $(`artist${deckId}`).textContent    = artist !== 'Unknown' ? artist : ''

      // Run BPM + key + energy analysis in Web Worker (non-blocking)
      updateAnalyzingStatus('BPM')
      const analysis = await analyzer.analyzeTrack(audioBuffer, (progress) => {
        updateAnalyzingStatus(progress.stage.toUpperCase().replace(' DETECTED', ''))
      })

      const bpm = analysis?.bpm || 128

      // Detect intro/outro boundaries (fast, main-thread RMS scan)
      const { introEnd, outroStart } = analyzer.detectIntroAndOutro(audioBuffer)
      if (analysis) { analysis.introEnd = introEnd; analysis.outroStart = outroStart }

      deck.loadBuffer(audioBuffer, bpm, track, artist)
      deck.analysis = analysis

      // Generate track ID and auto-load cues from database
      const fileSize = new Blob([arrayBuffer]).size
      const trackId = await deck.generateTrackId(file.name, fileSize, audioBuffer.duration)

      if (trackId && storage) {
        try {
          const savedCues = await storage.loadTrackCues(trackId)
          if (savedCues) {
            deck.restoreCueData(savedCues)
            // Update UI to show restored cue points
            if (savedCues.mainCue !== undefined && savedCues.mainCue !== null) {
              // Main cue restored (UI update happens on sync)
            }
            if (Array.isArray(savedCues.hotCues)) {
              // Hot cues restored (UI update happens on sync)
              for (let i = 0; i < 4; i++) {
                const btn = document.querySelector(`.hot-cue[data-deck="${deckId}"][data-index="${i}"]`)
                if (btn && savedCues.hotCues[i] !== null) {
                  btn.classList.add('has-cue')
                } else if (btn) {
                  btn.classList.remove('has-cue')
                }
              }
            }
          }
        } catch (err) {
          console.warn('Failed to load saved cues:', err)
        }
      }

      // Seek to introEnd so deck is cued at first musical moment
      if (introEnd > 0) deck.seekTo(introEnd)

      // Update UI with analysis results
      $(`bpmDisplay${deckId}`).textContent  = bpm + ' BPM'
      $(`keyDisplay${deckId}`).textContent  = analysis?.key || '-'
      $(`energyDisplay${deckId}`).textContent = (analysis?.energy ? (analysis.energy * 100).toFixed(0) : '--') + '%'
      $(`duration${deckId}`).textContent    = formatTime(audioBuffer.duration)
      $(`time${deckId}`).textContent        = '0:00'

      // Update compatibility score if both tracks loaded
      if (otherDeck.audioBuffer && otherDeck.analysis && deck.analysis) {
        const score = analyzer.scoreCompatibility(deck.analysis, otherDeck.analysis, bpm, otherDeck.bpm)
        $('compatibilityValue').textContent = score.toFixed(0) + '%'
      }

      // Update the placeholder in the playlist with real data (glow stops)
      if (playlist && placeholderIdx >= 0) {
        const trackData = playlist.getPlaylist()[placeholderIdx]
        if (trackData) {
          trackData.bpm        = bpm
          trackData.key        = analysis?.key    || '-'
          trackData.energy     = analysis?.energy || 0
          trackData.analysis   = analysis
          trackData.duration   = audioBuffer.duration
          trackData.audioBuffer = audioBuffer
          trackData.fileSize   = file.size
          trackData.introEnd   = introEnd
          trackData.outroStart = outroStart
          trackData._analyzing = false
          // Bitrate: actual file size × 8 bits ÷ duration ÷ 1000 = kbps
          trackData.bitrate    = Math.round((file.size * 8) / audioBuffer.duration / 1000)
        }
        renderPlaylist(playlistContainerId, playlist, deckId)
      }

      // Reset hot cue dots
      document.querySelectorAll(`.hot-cue[data-deck="${deckId}"]`).forEach(el => {
        el.classList.remove('has-cue')
      })

      // Reset tempo, sync
      $(`tempo${deckId}`).value          = 0
      $(`tempoLabel${deckId}`).textContent = '+0.0%'
      $(`sync${deckId}`).classList.remove('synced')

      // Album art → vinyl disc (non-blocking)
      APIClient.applyVinylArt($(`vinyl${deckId}`), artist, track, file.name)

    } catch (err) {
      console.error('Error loading track:', err)
      alert('Could not load track: ' + err.message)
    }
  }

  // ── File loaders (Deck A & B) ────────────────────────────────────────────

  bindDropZone('dropZoneA', 'fileInputA', async (files) => {
    // First file loads to deck, rest queue into playlist
    await loadTrack(files[0], deckA, 'A', deckB, playlistA, playlistB)
    for (let i = 1; i < files.length; i++) {
      await loadTrack(files[i], deckA, 'A', deckB, playlistA, playlistB)
    }
  })
  bindDropZone('dropZoneB', 'fileInputB', async (files) => {
    await loadTrack(files[0], deckB, 'B', deckA, playlistB, playlistA)
    for (let i = 1; i < files.length; i++) {
      await loadTrack(files[i], deckB, 'B', deckA, playlistB, playlistA)
    }
  })

  // ── Transport ────────────────────────────────────────────────────────────

  on('playA', 'click', () => {
    if (!deckA.audioBuffer) return
    deckA.togglePlay()
    syncPlayUI('A', deckA)
  })

  on('playB', 'click', () => {
    if (!deckB.audioBuffer) return
    deckB.togglePlay()
    syncPlayUI('B', deckB)
  })

  // Cue: left-click = jump to cue, right-click = set cue
  on('cueA', 'click', () => { if (deckA.audioBuffer) deckA.jumpToCue() })
  on('cueB', 'click', () => { if (deckB.audioBuffer) deckB.jumpToCue() })
  on('cueA', 'contextmenu', e => {
    e.preventDefault()
    if (deckA.audioBuffer) {
      deckA.setCue()
      saveTrackCuesToDB(deckA) // Auto-save main cue
    }
  })
  on('cueB', 'contextmenu', e => {
    e.preventDefault()
    if (deckB.audioBuffer) {
      deckB.setCue()
      saveTrackCuesToDB(deckB) // Auto-save main cue
    }
  })

  // Sync
  on('syncA', 'click', () => {
    if (!deckA.audioBuffer || !deckB.audioBuffer) return
    deckA.syncTo(deckB)
    $('syncA').classList.add('synced')
    $('syncB').classList.remove('synced')
    deckB.unsync()
    $('tempoA').value = ((deckA.playbackRate - 1) * 100).toFixed(1)
    $('tempoLabelA').textContent = formatPercent(deckA.playbackRate)
  })

  on('syncB', 'click', () => {
    if (!deckB.audioBuffer || !deckA.audioBuffer) return
    deckB.syncTo(deckA)
    $('syncB').classList.add('synced')
    $('syncA').classList.remove('synced')
    deckA.unsync()
    $('tempoB').value = ((deckB.playbackRate - 1) * 100).toFixed(1)
    $('tempoLabelB').textContent = formatPercent(deckB.playbackRate)
  })

  // ── Mix Points ────────────────────────────────────────────────────────────

  on('mixPointA', 'click', () => {
    if (!deckA.audioBuffer) return
    deckA.mixPoint = deckA.currentTime
    $('mixPointA').classList.add('active')
    $('mixPointA').title = `Mix point: ${formatTime(deckA.mixPoint)}`
  })

  on('mixPointB', 'click', () => {
    if (!deckB.audioBuffer) return
    deckB.mixPoint = deckB.currentTime
    $('mixPointB').classList.add('active')
    $('mixPointB').title = `Mix point: ${formatTime(deckB.mixPoint)}`
  })

  // ── Suggest Mix Point (Auto-detect) ────────────────────────────────────

  on('suggestMixA', 'click', () => {
    if (!deckA.audioBuffer) return
    const suggestedTime = analyzer.suggestMixPoint(deckA.audioBuffer, deckA.duration)
    if (suggestedTime !== null) {
      deckA.mixPoint = suggestedTime
      $('mixPointA').classList.add('active')
      $('mixPointA').title = `Mix point: ${formatTime(suggestedTime)} (suggested)`
    }
  })

  on('suggestMixB', 'click', () => {
    if (!deckB.audioBuffer) return
    const suggestedTime = analyzer.suggestMixPoint(deckB.audioBuffer, deckB.duration)
    if (suggestedTime !== null) {
      deckB.mixPoint = suggestedTime
      $('mixPointB').classList.add('active')
      $('mixPointB').title = `Mix point: ${formatTime(suggestedTime)} (suggested)`
    }
  })

  // ── Hot Cues ─────────────────────────────────────────────────────────────

  const hotCueTimestamps = {} // Track last right-click timestamp per button

  async function saveTrackCuesToDB(deck) {
    // Auto-save cues to IndexedDB when they change
    if (!deck.currentTrackId || !storage) return

    try {
      const cueData = deck.getCueData()
      await storage.saveTrackCues(deck.currentTrackId, cueData)
    } catch (err) {
      console.warn('Failed to save cues:', err)
    }
  }

  for (const id of ['A', 'B']) {
    const deck = id === 'A' ? deckA : deckB
    for (let i = 0; i < 4; i++) {
      const el = document.querySelector(`.hot-cue[data-deck="${id}"][data-index="${i}"]`)
      if (!el) continue

      const key = `${id}-${i}` // Unique identifier for each cue button

      el.addEventListener('click', () => {
        if (!deck.audioBuffer) return
        deck.jumpToHotCue(i)
      })

      el.addEventListener('contextmenu', e => {
        e.preventDefault()
        if (!deck.audioBuffer) return

        const now = Date.now()
        const lastTime = hotCueTimestamps[key] || 0

        // Double right-click within 300ms = clear cue
        if (now - lastTime < 300) {
          deck.clearHotCue(i)
          el.classList.remove('has-cue')
          hotCueTimestamps[key] = 0 // Reset timestamp
          saveTrackCuesToDB(deck) // Auto-save cleared cue
        } else {
          // Single right-click = set cue
          deck.setHotCue(i)
          el.classList.add('has-cue')
          hotCueTimestamps[key] = now
          saveTrackCuesToDB(deck) // Auto-save set cue
        }
      })
    }
  }

  // ── Loops ────────────────────────────────────────────────────────────────

  on('loopInA',     'click', () => { if (deckA.audioBuffer) deckA.setLoopIn() })
  on('loopOutA',    'click', () => { if (deckA.audioBuffer) { deckA.setLoopOut(); $('loopToggleA').classList.add('active') } })
  on('loopHalfA',   'click', () => { deckA.halveLoop() })
  on('loopDoubleA', 'click', () => { deckA.doubleLoop() })
  on('loopToggleA', 'click', () => {
    if (!deckA.audioBuffer) return
    deckA.toggleLoop()
    $('loopToggleA').classList.toggle('active', deckA.loopActive)
  })

  on('loopInB',     'click', () => { if (deckB.audioBuffer) deckB.setLoopIn() })
  on('loopOutB',    'click', () => { if (deckB.audioBuffer) { deckB.setLoopOut(); $('loopToggleB').classList.add('active') } })
  on('loopHalfB',   'click', () => { deckB.halveLoop() })
  on('loopDoubleB', 'click', () => { deckB.doubleLoop() })
  on('loopToggleB', 'click', () => {
    if (!deckB.audioBuffer) return
    deckB.toggleLoop()
    $('loopToggleB').classList.toggle('active', deckB.loopActive)
  })

  // ── Tempo ────────────────────────────────────────────────────────────────

  bindRange('tempoA', v => {
    deckA.setTempoOffset(v)
    $('tempoLabelA').textContent = formatPercent(deckA.playbackRate)
    $('syncA').classList.remove('synced')
  })

  bindRange('tempoB', v => {
    deckB.setTempoOffset(v)
    $('tempoLabelB').textContent = formatPercent(deckB.playbackRate)
    $('syncB').classList.remove('synced')
  })

  on('tapTempoA', 'click', () => {
    deckA.tapTempo()
    if (deckA.bpm) $('bpmDisplayA').textContent = deckA.bpm + ' BPM'
  })

  on('tapTempoB', 'click', () => {
    deckB.tapTempo()
    if (deckB.bpm) $('bpmDisplayB').textContent = deckB.bpm + ' BPM'
  })

  // ── Key Lock ────────────────────────────────────────────────────────────

  on('keyLockA', 'click', () => {
    const active = deckA.toggleKeyLock()
    $('keyLockA').classList.toggle('active', active)
  })

  on('keyLockB', 'click', () => {
    const active = deckB.toggleKeyLock()
    $('keyLockB').classList.toggle('active', active)
  })

  // ── Beat Jump ───────────────────────────────────────────────────────────

  on('beatJumpBackA',  'click', () => deckA.beatJump(-4))
  on('beatJumpBack1A', 'click', () => deckA.beatJump(-1))
  on('beatJumpFwd1A',  'click', () => deckA.beatJump(1))
  on('beatJumpFwdA',   'click', () => deckA.beatJump(4))

  on('beatJumpBackB',  'click', () => deckB.beatJump(-4))
  on('beatJumpBack1B', 'click', () => deckB.beatJump(-1))
  on('beatJumpFwd1B',  'click', () => deckB.beatJump(1))
  on('beatJumpFwdB',   'click', () => deckB.beatJump(4))

  // ── EQ Kill ──────────────────────────────────────────────────────────────

  for (const id of ['A', 'B']) {
    for (const band of ['Low', 'Mid', 'High']) {
      const btnId = `kill${band}${id}`
      on(btnId, 'click', () => {
        const btn = $(btnId)
        btn.classList.toggle('active')
        engine.killBand(id, band.toLowerCase(), btn.classList.contains('active'))
      })
    }
  }

  // ── Vocal Isolation ──────────────────────────────────────────────────────

  on('voxA', 'click', () => {
    if (!engine._voxAvailable) return
    const active = engine.toggleVocalIsolation('A')
    $('voxA').classList.toggle('active', active)
  })

  on('voxB', 'click', () => {
    if (!engine._voxAvailable) return
    const active = engine.toggleVocalIsolation('B')
    $('voxB').classList.toggle('active', active)
  })

  // Disable VOX buttons if worklet failed to load (check after init settles)
  setTimeout(() => {
    if (!engine._voxAvailable) {
      if ($('voxA')) $('voxA').classList.add('disabled')
      if ($('voxB')) $('voxB').classList.add('disabled')
    }
  }, 2000)

  // ── EQ Knobs ─────────────────────────────────────────────────────────────

  for (const id of ['A', 'B']) {
    bindRange(`eqLow${id}`,  v => engine.setEQ(id, 'low',  v))
    bindRange(`eqMid${id}`,  v => engine.setEQ(id, 'mid',  v))
    bindRange(`eqHigh${id}`, v => engine.setEQ(id, 'high', v))
  }

  // ── Filter ───────────────────────────────────────────────────────────────

  bindRange('filterA', v => {
    engine.setFilter('A', v / 100)
    $('filterLabelA').textContent = Math.round(v)
  })
  bindRange('filterB', v => {
    engine.setFilter('B', v / 100)
    $('filterLabelB').textContent = Math.round(v)
  })

  // ── Reverb ───────────────────────────────────────────────────────────────

  bindRange('reverbA', v => {
    engine.setReverb('A', v / 100)
    $('reverbLabelA').textContent = Math.round(v)
  })
  bindRange('reverbB', v => {
    engine.setReverb('B', v / 100)
    $('reverbLabelB').textContent = Math.round(v)
  })

  // ── Channel faders ───────────────────────────────────────────────────────

  bindRange('faderA', v => {
    engine.setDeckVolume('A', v / 100)
    $('faderLabelA').textContent = Math.round(v)
  })
  bindRange('faderB', v => {
    engine.setDeckVolume('B', v / 100)
    $('faderLabelB').textContent = Math.round(v)
  })

  // ── Crossfader ───────────────────────────────────────────────────────────

  bindRange('crossfader', v => engine.setCrossfader(v / 100))

  // ── Master Volume ────────────────────────────────────────────────────────

  bindRange('masterVolume', v => {
    engine.setMasterVolume(v / 100)
    $('masterVolumeLabel').textContent = Math.round(v) + '%'
  })

  // ── Auto-Mix ─────────────────────────────────────────────────────────────

  let _autoMixInterval = null
  on('autoMixBtn', 'click', () => {
    const btn = $('autoMixBtn')
    btn.classList.toggle('active')

    if (btn.classList.contains('active')) {
      _autoMixInterval = startAutoMix(deckA, deckB, engine, analyzer, playlistA, playlistB, renderPlaylist, loadPlaylistTrack, syncPlayUI)
      state.updateMixer({ autoMixActive: true })
    } else {
      clearInterval(_autoMixInterval)
      state.updateMixer({ autoMixActive: false })
    }
  })

  // ── Save / Load ───────────────────────────────────────────────────────────

  on('saveMixBtn', 'click', async () => {
    const name = prompt('Name this mix:')
    if (!name?.trim()) return

    const id = await storage.saveMix({
      name: name.trim(),
      tracks: state.decks,
      mixSettings: state.mixer,
    })

    alert(`Mix "${name.trim()}" saved!`)
    await refreshMixDropdown(storage)
  })

  on('savedMixes', 'change', async e => {
    const id = parseInt(e.target.value)
    if (!id) return
    const mix = await storage.loadMix(id)
    if (mix) {
      // Restore track names + BPM labels from saved mix
      const a = mix.tracks?.A
      const b = mix.tracks?.B
      if (a?.trackName) {
        $('trackNameA').textContent = a.trackName
        $('artistA').textContent    = a.artist || ''
        $('bpmDisplayA').textContent = a.bpm ? a.bpm + ' BPM' : '--- BPM'
      }
      if (b?.trackName) {
        $('trackNameB').textContent = b.trackName
        $('artistB').textContent    = b.artist || ''
        $('bpmDisplayB').textContent = b.bpm ? b.bpm + ' BPM' : '--- BPM'
      }
    }
    e.target.value = ''
  })

  // ── Export ───────────────────────────────────────────────────────────────

  on('exportBtn', 'click', async () => {
    if (!deckA.audioBuffer && !deckB.audioBuffer) {
      alert('Load at least one track before exporting.')
      return
    }

    showLoading('RENDERING MIX...')
    $('exportBtn').disabled = true

    try {
      const blob = await engine.exportMix(deckA, deckB)
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = url
      a.download = `dj-mix-${Date.now()}.wav`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      alert('Export failed: ' + err.message)
      console.error(err)
    } finally {
      hideLoading()
      $('exportBtn').disabled = false
    }
  })

  // ── Playlist sorting ───────────────────────────────────────────────────────

  on('sortBpmA', 'click', () => {
    playlistA.sortByBPM()
    renderPlaylist('playlistA', playlistA, 'A')
  })

  on('sortKeyA', 'click', () => {
    playlistA.sortByKey()
    renderPlaylist('playlistA', playlistA, 'A')
  })

  on('sortEnergyA', 'click', () => {
    playlistA.sortByEnergy()
    renderPlaylist('playlistA', playlistA, 'A')
  })

  on('sortOptimalA', 'click', () => {
    playlistA.sortDJOptimal()
    renderPlaylist('playlistA', playlistA, 'A')
  })

  on('sortBpmB', 'click', () => {
    playlistB.sortByBPM()
    renderPlaylist('playlistB', playlistB, 'B')
  })

  on('sortKeyB', 'click', () => {
    playlistB.sortByKey()
    renderPlaylist('playlistB', playlistB, 'B')
  })

  on('sortEnergyB', 'click', () => {
    playlistB.sortByEnergy()
    renderPlaylist('playlistB', playlistB, 'B')
  })

  on('sortOptimalB', 'click', () => {
    playlistB.sortDJOptimal()
    renderPlaylist('playlistB', playlistB, 'B')
  })

  // ── Time display loop (rAF) ───────────────────────────────────────────────

  let deckATrackEnded = false
  let deckBTrackEnded = false

  function updateTimeDisplays() {
    if (deckA.isPlaying || deckA.startOffset > 0) {
      $('timeA').textContent = formatTime(deckA.currentTime)
      const remainingA = Math.max(0, deckA.duration - deckA.currentTime)
      $('remainingA').textContent = '-' + formatTime(remainingA)

      // Auto-advance to next track when current finishes
      if (deckA.isPlaying && deckA.currentTime >= deckA.duration - 0.1 && !deckATrackEnded) {
        deckATrackEnded = true
        const nextTrack = playlistA.nextTrack()
        if (nextTrack) {
          loadPlaylistTrack(nextTrack, playlistA, playlistA.currentIndex, deckA, 'A', engine, state, analyzer, playlistA, playlistB)
          renderPlaylist('playlistA', playlistA, 'A')
          // Auto-play the loaded track, then reset flag
          setTimeout(() => { deckA.play(); syncPlayUI('A', deckA); deckATrackEnded = false }, 100)
        } else {
          deckATrackEnded = false
        }
      } else if (!deckA.isPlaying || deckA.currentTime < deckA.duration - 0.1) {
        deckATrackEnded = false
      }
    }
    if (deckB.isPlaying || deckB.startOffset > 0) {
      $('timeB').textContent = formatTime(deckB.currentTime)
      const remainingB = Math.max(0, deckB.duration - deckB.currentTime)
      $('remainingB').textContent = '-' + formatTime(remainingB)

      // Auto-advance to next track when current finishes
      if (deckB.isPlaying && deckB.currentTime >= deckB.duration - 0.1 && !deckBTrackEnded) {
        deckBTrackEnded = true
        const nextTrack = playlistB.nextTrack()
        if (nextTrack) {
          loadPlaylistTrack(nextTrack, playlistB, playlistB.currentIndex, deckB, 'B', engine, state, analyzer, playlistA, playlistB)
          renderPlaylist('playlistB', playlistB, 'B')
          // Auto-play the loaded track, then reset flag
          setTimeout(() => { deckB.play(); syncPlayUI('B', deckB); deckBTrackEnded = false }, 100)
        } else {
          deckBTrackEnded = false
        }
      } else if (!deckB.isPlaying || deckB.currentTime < deckB.duration - 0.1) {
        deckBTrackEnded = false
      }
    }
    requestAnimationFrame(updateTimeDisplays)
  }
  updateTimeDisplays()

  // ── Waveform click-to-seek ────────────────────────────────────────────────

  const canvasA = $('waveform-a')
  const canvasB = $('waveform-b')

  function onWaveformClick(e, deck, deckName) {
    if (!deck.duration) return // no track loaded
    const rect = e.target.getBoundingClientRect()
    const x = e.clientX - rect.left
    const fraction = x / rect.width
    const newTime = Math.max(0, Math.min(fraction * deck.duration, deck.duration))
    deck.seekTo(newTime)
    $('time' + deckName).textContent = formatTime(newTime)
  }

  canvasA.addEventListener('click', (e) => onWaveformClick(e, deckA, 'A'))
  canvasB.addEventListener('click', (e) => onWaveformClick(e, deckB, 'B'))

  // Add cursor hint
  canvasA.style.cursor = 'pointer'
  canvasB.style.cursor = 'pointer'

  // ── Search UI & Download Manager (music search + YouTube audio download) ──
  const downloadManager = new DownloadManager(storage, engine.ctx)
  initializeSearchUI(storage, downloadManager)

  // Listen for search results added to playlist
  document.addEventListener('playlistTrackAdded', (e) => {
    const { deckId, track } = e.detail
    const playlist = deckId === 'A' ? playlistA : playlistB

    playlist.addTrack(track)
    renderPlaylist(`playlist${deckId}`, playlist, deckId)
  })

  // Listen for download progress updates
  document.addEventListener('downloadProgress', (e) => {
    const { trackId, progress, status } = e.detail
    console.log(`📥 ${trackId}: ${progress}% (${status})`)
    // Find and update track in both playlists
    ;[playlistA, playlistB].forEach((pl, idx) => {
      const deckId = idx === 0 ? 'A' : 'B'
      pl.getPlaylist().forEach(track => {
        if (track.searchSourceId === trackId) {
          track._downloadProgress = progress
          track._downloadStatus = status
          renderPlaylist(`playlist${deckId}`, pl, deckId)
        }
      })
    })
  })

  // Listen for download completion
  document.addEventListener('downloadComplete', async (e) => {
    const { trackId, audioBuffer, preChecks, postChecks } = e.detail
    console.log(`✓ Download complete: ${trackId}`)

    for (const [pl, deckId] of [[playlistA, 'A'], [playlistB, 'B']]) {
      for (const track of pl.getPlaylist()) {
        if (track.searchSourceId !== trackId || !track._isDownloading) continue

        // Transition: downloading → analyzing
        track.audioBuffer      = audioBuffer
        track._isDownloading   = false
        track._analyzing       = true
        track._downloadStatus  = 'COMPLETE'
        track.duration         = audioBuffer.duration
        renderPlaylist(`playlist${deckId}`, pl, deckId)

        try {
          // Run BPM + key + energy in Web Worker (same pipeline as file upload)
          const analysis = await analyzer.analyzeTrack(audioBuffer, (progress) => {
            // Optional: could surface progress here
          })

          // Detect intro/outro boundaries
          const { introEnd, outroStart } = analyzer.detectIntroAndOutro(audioBuffer)

          track.bpm        = analysis?.bpm    || null
          track.key        = analysis?.key    || null
          track.energy     = analysis?.energy || 0
          track.introEnd   = introEnd
          track.outroStart = outroStart
          track._analyzing = false
          // Bitrate estimate for downloaded audio (PCM size → compressed kbps approximation)
          const rawBytes = audioBuffer.length * audioBuffer.numberOfChannels * 2
          track.bitrate  = Math.round((rawBytes * 8) / audioBuffer.duration / 1000)
          // Build track.analysis object so loadPlaylistTrack fast-path triggers
          track.analysis = {
            bpm:              track.bpm,
            key:              track.key,
            energy:           track.energy,
            spectralCentroid: analysis?.spectralCentroid || 0,
            introEnd,
            outroStart
          }

          renderPlaylist(`playlist${deckId}`, pl, deckId)
          console.log(`✓ Analysis complete: ${track.name} — ${track.bpm} BPM, ${track.key}`)
        } catch (err) {
          console.warn(`⚠️ Analysis failed for ${track.name}:`, err.message)
          track._analyzing = false
          renderPlaylist(`playlist${deckId}`, pl, deckId)
        }
      }
    }
  })

  // Listen for download failures
  document.addEventListener('downloadFailed', (e) => {
    const { trackId, error } = e.detail
    console.error(`❌ Download failed: ${trackId}`, error)
    alert(`Download failed for track: ${error}`)

    // Mark track as failed in playlist
    ;[playlistA, playlistB].forEach((pl, idx) => {
      const deckId = idx === 0 ? 'A' : 'B'
      pl.getPlaylist().forEach(track => {
        if (track.searchSourceId === trackId) {
          track._isDownloading = false
          track._downloadStatus = 'ERROR'
          renderPlaylist(`playlist${deckId}`, pl, deckId)
        }
      })
    })
  })

  // ── Initial state ─────────────────────────────────────────────────────────
  refreshMixDropdown(storage)
}

// ─── Vinyl + play button sync ─────────────────────────────────────────────────

function syncPlayUI(id, deck) {
  const panel  = document.getElementById(`deck${id}-panel`)
  const playBtn = document.getElementById(`play${id}`)
  const icon    = document.getElementById(`playIcon${id}`)

  panel.classList.toggle('playing', deck.isPlaying)
  playBtn.classList.toggle('playing', deck.isPlaying)
  icon.innerHTML = deck.isPlaying ? '&#9646;&#9646;' : '&#9654;'
}

// ─── Drop zone ────────────────────────────────────────────────────────────────

function bindDropZone(zoneId, inputId, onFiles) {
  const zone  = $(zoneId)
  const input = $(inputId)
  if (!zone || !input) return

  zone.addEventListener('click', () => input.click())

  zone.addEventListener('dragover', e => {
    e.preventDefault()
    zone.classList.add('dragover')
  })
  zone.addEventListener('dragleave', () => zone.classList.remove('dragover'))
  zone.addEventListener('drop', e => {
    e.preventDefault()
    zone.classList.remove('dragover')
    const files = Array.from(e.dataTransfer.files)
    if (files.length) onFiles(files)
  })

  input.addEventListener('change', e => {
    const files = Array.from(e.target.files)
    if (files.length) onFiles(files)
    input.value = ''   // allow same file again
  })
}

// ─── Auto-Mix (intelligent DJ transitions) ──────────────────────────────────

function startAutoMix(deckA, deckB, engine, analyzer, playlistA, playlistB, renderPlaylist, loadPlaylistTrack, syncPlayUI) {
  let fadeActive = false
  const FALLBACK_TRIGGER = 30  // seconds before end if no outroStart detected

  // How many seconds before end should we start the fade?
  function triggerRemaining(deck) {
    const outroStart = deck.analysis?.outroStart
    if (outroStart && outroStart > 0 && outroStart < deck.duration - 5) {
      // Trigger when we reach the detected outro start point
      return deck.duration - outroStart
    }
    return FALLBACK_TRIGGER
  }

  return setInterval(() => {
    if (fadeActive) return

    // Determine which deck is the source (playing & near end / at outro)
    let src, tgt, srcId, tgtId, xfTarget, srcPl, tgtPl
    const trigA = triggerRemaining(deckA)
    const trigB = triggerRemaining(deckB)
    if (deckA.isPlaying && deckA.audioBuffer && deckA.remaining <= trigA && deckA.remaining > 0) {
      src = deckA; tgt = deckB; srcId = 'A'; tgtId = 'B'; xfTarget = 1.0
      srcPl = playlistA; tgtPl = playlistB
    } else if (deckB.isPlaying && deckB.audioBuffer && deckB.remaining <= trigB && deckB.remaining > 0) {
      src = deckB; tgt = deckA; srcId = 'B'; tgtId = 'A'; xfTarget = 0.0
      srcPl = playlistB; tgtPl = playlistA
    } else {
      return
    }

    fadeActive = true

    // ── Auto-load next track on target deck if empty ──
    if (!tgt.audioBuffer) {
      const next = tgtPl ? tgtPl.nextTrack() : null
      const loadNext = next || (srcPl ? srcPl.nextTrack() : null)
      const loadPl   = next ? tgtPl : srcPl
      if (loadNext && loadPl) {
        loadPlaylistTrack(loadNext, loadPl, loadPl.currentIndex, tgt, tgtId, engine, null, analyzer, playlistA, playlistB)
        renderPlaylist(`playlist${tgtId}`, tgtPl, tgtId)
        // Seek to introEnd so we start at first musical moment
        if (loadNext.introEnd > 0) {
          setTimeout(() => tgt.seekTo(loadNext.introEnd), 80)
        }
      } else {
        fadeActive = false
        return  // Nothing to mix into
      }
    } else if (tgt.analysis?.introEnd > 0 && !tgt.isPlaying) {
      // Target has a track loaded but not playing — seek to its introEnd
      tgt.seekTo(tgt.analysis.introEnd)
    }

    // ── Intelligent BPM matching ──
    // Gradually ramp target deck's tempo to match source BPM, then ease to target's natural BPM
    const srcBPM = src.bpm || 128
    const tgtBPM = tgt.bpm || 128
    const bpmDiff = Math.abs(srcBPM - tgtBPM)
    const bpmRatio = srcBPM / tgtBPM

    // Only tempo-match if BPM difference is within ±15% (otherwise tracks are too different)
    const shouldMatchBPM = bpmDiff > 2 && bpmDiff / srcBPM < 0.15

    if (shouldMatchBPM) {
      // Start target at source's BPM, then ease to its own BPM during fade
      tgt.playbackRate = bpmRatio
      if (tgt._source) tgt._source.playbackRate.value = bpmRatio
    }

    // ── Compute fade duration based on compatibility ──
    let fadeDuration = 12  // default
    if (src.analysis && tgt.analysis) {
      const score = analyzer.scoreCompatibility(src.analysis, tgt.analysis, srcBPM, tgtBPM)
      // Higher compatibility → longer, smoother fade (8-16s)
      // Lower compatibility → shorter, snappier fade (4-8s)
      fadeDuration = score > 70 ? 16 : score > 40 ? 10 : 6
    }

    // ── Start target deck ──
    if (tgt.audioBuffer && !tgt.isPlaying) {
      tgt.play()
      syncPlayUI(tgtId, tgt)
    }

    // ── Animate crossfade + BPM ramp ──
    const startXf   = parseFloat($('crossfader').value) / 100
    const startCtxT = engine.ctx.currentTime
    const startRate = tgt.playbackRate

    const tick = setInterval(() => {
      const elapsed = engine.ctx.currentTime - startCtxT
      const t       = Math.min(elapsed / fadeDuration, 1)

      // Equal-power crossfade with S-curve easing
      const eased = t < 0.5
        ? 2 * t * t                        // ease in
        : 1 - Math.pow(-2 * t + 2, 2) / 2  // ease out
      const val = startXf + (xfTarget - startXf) * eased
      engine.setCrossfader(val)
      $('crossfader').value = val * 100

      // BPM ramp: ease target deck from source BPM to its natural BPM
      if (shouldMatchBPM) {
        const rampedRate = startRate + (1.0 - startRate) * t
        tgt.playbackRate = rampedRate
        if (tgt._source) tgt._source.playbackRate.value = rampedRate

        // Update tempo slider to reflect the ramp
        const pct = (rampedRate - 1) * 100
        $(`tempo${tgtId}`).value = pct
        $(`tempoLabel${tgtId}`).textContent = (pct >= 0 ? '+' : '') + pct.toFixed(1) + '%'
      }

      if (t >= 1) {
        clearInterval(tick)

        // Reset target deck to natural tempo
        tgt.playbackRate = 1.0
        if (tgt._source) tgt._source.playbackRate.value = 1.0
        $(`tempo${tgtId}`).value = 0
        $(`tempoLabel${tgtId}`).textContent = '+0.0%'

        // Stop source deck
        src.pause()
        syncPlayUI(srcId, src)

        // Center crossfader for next mix
        engine.setCrossfader(0.5)
        $('crossfader').value = 50

        // Auto-load next track on the now-idle source deck
        const nextForSrc = srcPl ? srcPl.nextTrack() : null
        if (nextForSrc) {
          loadPlaylistTrack(nextForSrc, srcPl, srcPl.currentIndex, src, srcId, engine, null, analyzer, playlistA, playlistB)
          renderPlaylist(`playlist${srcId}`, srcPl, srcId)
        }

        fadeActive = false
      }
    }, 50)
  }, 1000)
}

// ─── Mix dropdown ─────────────────────────────────────────────────────────────

async function refreshMixDropdown(storage) {
  const mixes = await storage.getAllMixes()
  const sel   = $('savedMixes')
  sel.innerHTML = '<option value="">— Load Mix —</option>'
  mixes.forEach(m => {
    const opt   = document.createElement('option')
    opt.value   = m.id
    opt.textContent = `${m.name} (${new Date(m.createdAt).toLocaleDateString()})`
    sel.appendChild(opt)
  })
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatPercent(rate) {
  const pct = (rate - 1) * 100
  return (pct >= 0 ? '+' : '') + pct.toFixed(1) + '%'
}
